import {
  defined,
  CameraEventAggregator,
  Cartesian2,
  Cartesian3,
  Viewer,
  Ray,
  IntersectionTests,
} from "cesium";
import { CameraEventTypeWithOptionalModifier } from "lib/cesium/controls/ControlsPreset";
import { CameraOrbitController } from "./CameraOrbitController";
import {
  CONTROLS_PRESETS,
  DEFAULT_CONTROLS_PRESET,
} from "./ControlsPresetToolPlugin";

const DEFAULT_CONTROLS_PRESET_MAPPINGS =
  CONTROLS_PRESETS[DEFAULT_CONTROLS_PRESET].mappings;

export class CustomScreenSpaceCameraController {
  public static readonly LOOK_AROUND_MOVEMENT_SPEED = 1;
  /**
   * How far the mouse must move with the button held before it counts as an
   * orbit. Below this it's a click, so the slight jitter of an ordinary click
   * doesn't nudge the camera or flash the orbit marker.
   */
  public static readonly ORBIT_DRAG_THRESHOLD_PIXELS = 4;

  private readonly _viewer: Viewer;
  private readonly _picker: ScreenSpaceCameraPicker;
  private readonly _orbitController: CameraOrbitController;
  private readonly _orbitPivot = new Cartesian3();
  private readonly _prevButtonDown: Map<string, boolean> = new Map();
  private readonly _cameraEventAggregator: CameraEventAggregator;
  private readonly _detachTickHandlerFunc: () => void;

  private _isOrbiting: boolean = false;
  private readonly _orbitPressPosition = new Cartesian2();
  private _positionHistory: Cartesian2[] = [];
  private _lookAroundControlsMappings: CameraEventTypeWithOptionalModifier[] =
    DEFAULT_CONTROLS_PRESET_MAPPINGS.lookAround.cesiumInputs;
  private _panControlsMappings: CameraEventTypeWithOptionalModifier[] =
    DEFAULT_CONTROLS_PRESET_MAPPINGS.pan.cesiumInputs;
  private _orbitControlsMappings: CameraEventTypeWithOptionalModifier[] =
    DEFAULT_CONTROLS_PRESET_MAPPINGS.orbitAroundPoint.cesiumInputs;
  private _zoomControlsMappings: CameraEventTypeWithOptionalModifier[] =
    DEFAULT_CONTROLS_PRESET_MAPPINGS.zoom.cesiumInputs;

  constructor(viewer: Viewer, cameraEventAggregator: CameraEventAggregator) {
    this._viewer = viewer;
    this._picker = new ScreenSpaceCameraPicker(this._viewer);
    this._orbitController = new CameraOrbitController(this._viewer);
    this._cameraEventAggregator = cameraEventAggregator;
    this._detachTickHandlerFunc = viewer.clock.onTick.addEventListener(
      this.tick.bind(this),
    );
  }

  get isMoving() {
    return !Cartesian2.equals(
      this._positionHistory[0],
      this._positionHistory[1],
    );
  }

  get isOrbiting() {
    return this._isOrbiting;
  }

  get orbitPivot(): Cartesian3 | null {
    if (this._orbitPivot.equals(Cartesian3.ZERO)) return null;
    return this._orbitPivot.clone();
  }

  destroy() {
    this._detachTickHandlerFunc?.();
  }

  setControlsMappings(
    mappings: Partial<
      Record<
        "lookAround" | "orbit" | "pan" | "zoom",
        CameraEventTypeWithOptionalModifier[]
      >
    >,
  ) {
    if (!mappings) return;
    if (mappings.lookAround)
      this._lookAroundControlsMappings = mappings.lookAround;
    if (mappings.orbit) this._orbitControlsMappings = mappings.orbit;
    if (mappings.pan) this._panControlsMappings = mappings.pan;
    if (mappings.zoom) this._zoomControlsMappings = mappings.zoom;
  }

  private tick() {
    if (!this._viewer.scene.screenSpaceCameraController.enableInputs) return;
    this.updateMovementTracking();
    this.tickLookAround();
    this.tickPanAndZoom();
    this.tickOrbit();
  }

  private tickLookAround() {
    for (const mapping of this._lookAroundControlsMappings) {
      const { isButtonDown } = this.isButtonDown(mapping);
      if (!isButtonDown || !this.isMoving) continue;

      const movement = this.getMovement(mapping);
      const deltaX = movement.endPosition.x - movement.startPosition.x;
      const deltaY = movement.endPosition.y - movement.startPosition.y;
      const x =
        (deltaX / this._viewer.canvas.clientWidth) *
        CustomScreenSpaceCameraController.LOOK_AROUND_MOVEMENT_SPEED;
      const y =
        -1 *
        (deltaY / this._viewer.canvas.clientHeight) *
        CustomScreenSpaceCameraController.LOOK_AROUND_MOVEMENT_SPEED;

      const orientation = {
        roll: 0,
        pitch: this._viewer.camera.pitch - y,
        heading: this._viewer.camera.heading - x,
      };
      this._viewer.camera.setView({ orientation });
    }
  }

  private tickPanAndZoom() {
    const mappings = this._panControlsMappings.concat(
      this._zoomControlsMappings,
    );
    for (const mapping of mappings) {
      const { isButtonDown, isButtonDownStart } = this.isButtonDown(mapping);
      if (!isButtonDown) continue;
      if (isButtonDownStart) {
        this._picker.pickPosition(
          this._cameraEventAggregator.currentMousePosition,
          this._orbitPivot,
        );
      }
    }
  }

  private tickOrbit() {
    const wasOrbiting = this._isOrbiting;
    this._isOrbiting = false;
    for (const mapping of this._orbitControlsMappings) {
      const { isButtonDown, isButtonDownStart } = this.isButtonDown(mapping);
      if (!isButtonDown) continue;
      if (isButtonDownStart) {
        this._picker.pickPosition(
          this._cameraEventAggregator.currentMousePosition,
          this._orbitPivot,
        );
        Cartesian2.clone(
          this._cameraEventAggregator.currentMousePosition,
          this._orbitPressPosition,
        );
      }

      const hasPivot = !this._orbitPivot.equals(Cartesian3.ZERO);
      if (!hasPivot) continue;

      if (wasOrbiting) this._isOrbiting = true;
      if (!this.isMoving) continue;

      // Don't start orbiting until the press has become a real drag.
      const distanceFromPress = Cartesian2.distance(
        this._cameraEventAggregator.currentMousePosition,
        this._orbitPressPosition,
      );
      if (
        !wasOrbiting &&
        distanceFromPress <
          CustomScreenSpaceCameraController.ORBIT_DRAG_THRESHOLD_PIXELS
      ) {
        continue;
      }

      this._isOrbiting = true;
      const movement = this.getMovement(mapping);
      this._orbitController.orbit(movement, this._orbitPivot);
    }
  }

  private isButtonDown(mapping: CameraEventTypeWithOptionalModifier) {
    const isButtonDown = this._cameraEventAggregator.isButtonDown(
      mapping.eventType,
      mapping.modifier,
    );
    let isButtonDownStart = false;
    const key = `${mapping.eventType}_${mapping.modifier ?? "none"}`;
    const wasButtonDown = this._prevButtonDown.get(key) || false;
    if (!isButtonDown && wasButtonDown) {
      this._prevButtonDown.set(key, false);
    } else if (isButtonDown && !wasButtonDown) {
      this._prevButtonDown.set(key, true);
      isButtonDownStart = true;
    }
    return { isButtonDown, isButtonDownStart };
  }

  private getMovement(mapping: CameraEventTypeWithOptionalModifier): {
    startPosition: Cartesian2;
    endPosition: Cartesian2;
  } {
    return this._cameraEventAggregator.getMovement(
      mapping.eventType,
      mapping.modifier,
    );
  }

  /**
   * Check whether the mouse is moving by comparing the current mouse position to the previous.
   * The camera event aggregator class isMoving only checks if a mouse button down
   * or touch has started and has been moved.
   *  */
  private updateMovementTracking() {
    this._positionHistory = [
      this._positionHistory[1],
      this._cameraEventAggregator.currentMousePosition.clone(),
    ];
  }
}

class ScreenSpaceCameraPicker {
  private readonly _viewer: Viewer;

  private readonly _scratchRay = new Ray();
  private readonly _scratchDepthIntersection = new Cartesian3();
  private readonly _scratchRayIntersection = new Cartesian3();

  constructor(viewer: Viewer) {
    this._viewer = viewer;
  }

  pickPosition(
    screenPosition: Cartesian2,
    result: Cartesian3,
  ): Cartesian3 | undefined {
    let position: Cartesian3 = null;

    const scene = this._viewer.scene;

    let depthIntersection: Cartesian3 = undefined;
    if (scene.pickPositionSupported) {
      depthIntersection = scene.pickPositionWorldCoordinates(
        screenPosition,
        this._scratchDepthIntersection,
      );
    }

    if (scene.cameraUnderground || !scene.globe?.show) {
      return defined(depthIntersection)
        ? Cartesian3.clone(depthIntersection, result)
        : null;
    }

    const ray = scene.camera.getPickRay(screenPosition, this._scratchRay);
    const cullBackFaces = true;
    let rayIntersection = scene.globe.pickWorldCoordinates(
      ray,
      scene,
      cullBackFaces,
      this._scratchRayIntersection,
    );

    if (!defined(rayIntersection)) {
      const ellipsoidIntersections = IntersectionTests.rayEllipsoid(
        ray,
        scene.ellipsoid,
      );
      if (defined(ellipsoidIntersections)) {
        rayIntersection = Ray.getPoint(
          ray,
          ellipsoidIntersections.start,
          this._scratchRayIntersection,
        );
      }
    }

    const pickDistance = defined(depthIntersection)
      ? Cartesian3.distance(depthIntersection, scene.camera.positionWC)
      : Number.POSITIVE_INFINITY;
    const rayDistance = defined(rayIntersection)
      ? Cartesian3.distance(rayIntersection, scene.camera.positionWC)
      : Number.POSITIVE_INFINITY;

    if (
      pickDistance !== Number.POSITIVE_INFINITY ||
      rayDistance !== Number.POSITIVE_INFINITY
    ) {
      if (pickDistance < rayDistance) {
        position = Cartesian3.clone(depthIntersection, result);
      } else {
        position = Cartesian3.clone(rayIntersection, result);
      }
    }
    return position;
  }
}
